// Cotización EN SERVIDOR de una línea armada por zonas.
//
// FUENTE ÚNICA de las filas de `order_item_modifiers` que se insertan cuando el ítem
// es zoneado. Las nueve rutas que hoy releen `modifier_options.price_delta` (§2.2 del
// spec) pasan por aquí, o cada una implementaría D7/D10/is_removal por su cuenta y
// habría nueve verdades de precio.
//
// Diseño: docs/superpowers/specs/2026-07-29-pizza-zoned-modifiers-design.md (§6, §7)
//
// 🔴 EL SERVIDOR NO CONFÍA EN EL PAYLOAD. Ni en el precio, ni en `is_included`, ni en
// `is_removal` como excusa para cobrar, ni en el tamaño (`resolveLineSize`). El body del
// request es entrada controlada por el cliente; el reductor del armador puede coincidir,
// pero no es autoridad.

import { effectiveUnitPrice, type PricingConfig } from "@/lib/utils/pricing"
import {
  ZONES_BY_LAYOUT,
  aggregateZonedSelections,
  collapseFullCoverage,
  zoneFraction,
  resolveZonedUnitPriceCents,
  type Zone,
  type ZoneLayout,
  type ZonedPricingMode,
  type ZonedSelection,
} from "@/lib/menu/zonedPricing"

/** Selección tal como llega en el body. `price_delta` del cliente se IGNORA. */
export type ZonedSelectionInput = {
  modifier_option_id: string
  modifier_group_id?: string | null
  option_name: string
  quantity?: number | null
  zone?: string | null
  is_removal?: boolean | null
  serve_on_side?: boolean | null
}

/** Lo que el servidor sabe de una opción, leído de la DB. */
export type ServerOptionFacts = {
  id: string
  group_id: string | null
  /** `modifier_options.price_delta` — el fallback de D10 y el precio de un ítem no zoneado. */
  price_delta: number | null
  /** D7: los incluidos valen 0 en cualquier zona. Sale de la DB, jamás del payload. */
  is_default: boolean | null
  /** D8: masa delgada a la izquierda y gruesa a la derecha es físicamente imposible. */
  zonable: boolean | null
}

/** Celda de la rejilla precio×tamaño (`modifier_option_size_prices`). */
export type SizePriceCell = {
  option_id: string
  size_option_id: string
  price_delta: number | null
}

export type ZonedQuoteContext = {
  /** `items.zoned_build`. Falso ⇒ la línea NO puede llevar disposición distinta de whole. */
  itemZonedBuild: boolean
  /** Disposición pedida para la línea. */
  layout: ZoneLayout
  /** `locations.zoned_pricing_mode`. */
  mode: ZonedPricingMode
  cfg: PricingConfig
  optionFacts: Map<string, ServerOptionFacts>
  /** Clave `optionId|sizeOptionId`. */
  sizeGrid: Map<string, SizePriceCell>
  /** Reglas min/max por grupo del ítem. Vacío o ausente ⇒ no se valida nada (paridad
   *  con las rutas que hoy no validan; §11 dice que no se ensancha el alcance). */
  groupRules?: Map<string, ZonedGroupRule>
  /**
   * `items.zone_size_group_id`. La opción de la línea que pertenece a este grupo es el
   * tamaño que gobierna la rejilla (`resolveLineSize`), y el grupo lleva max = 1 por
   * LÍNEA, no por zona (D5).
   *
   * 🔴 OBLIGATORIO a propósito — `null` si el ítem no tiene grupo de tamaño. Si fuera
   * opcional, la ruta que lo olvidara cobraría el precio plano en silencio; así lo caza tsc.
   */
  sizeGroupId: string | null
  /** Solo para el texto del error. */
  itemName?: string | null
}

/** Subconjunto de `modifier_groups` que la validación necesita. Mismo shape que
 *  ModifierGroupRule de validateModifierSelections, a propósito: son la misma regla. */
export type ZonedGroupRule = {
  is_required: boolean | null
  min_selections: number | null
  max_selections: number | null
}

/** Fila lista para insertar en `order_item_modifiers`, con el snapshot completo. */
export type ZonedModifierInsert = {
  modifier_option_id: string
  modifier_group_id: string | null
  option_name: string
  quantity: number
  serve_on_side: boolean
  zone: Zone
  zone_layout: ZoneLayout
  zone_fraction: number
  pricing_mode: ZonedPricingMode
  is_removal: boolean
  /** Valor CRUDO usado como base, antes de effectiveUnitPrice y antes de la fracción. */
  base_price_delta: number
  /** Lo que se cobra POR UNIDAD, ya con tamaño y fracción aplicados. */
  price_delta: number
  size_option_id: string | null
}

export type ZonedQuoteViolation = {
  error: "zoned_selection_invalid"
  detail: string
  status: 422
}

export type ZonedQuoteResult =
  | { ok: true; rows: ZonedModifierInsert[] }
  | { ok: false; violation: ZonedQuoteViolation }

const VALID_ZONES: readonly string[] = ["whole", "left", "right", "q1", "q2", "q3", "q4"]

/**
 * Candado de D9 para las rutas de ENMIENDA.
 *
 * Una línea armada por zonas **no se amenda: se reemplaza entera** (§6.3). Las rutas
 * de amend / edit-item no conocen zonas: recalcularían la línea desde el catálogo vivo
 * y aplanarían el armado en silencio — la cocina recibiría otra pizza y el cliente
 * pagaría precio de pizza entera por media.
 *
 * Hasta que exista el reemplazo de línea, estas rutas FALLAN CERRADO sobre una línea
 * zoneada. Es un 422 explicable, no una pizza equivocada.
 *
 * Se decide con la disposición de la LÍNEA EN LA BASE, nunca con el payload: el punto
 * es proteger lo que ya está vendido de un cliente que no sabe de zonas.
 */
export function rejectZonedAmend(
  storedLayout: string | null | undefined,
  itemName?: string | null
): ZonedQuoteViolation | null {
  if (storedLayout !== "halves" && storedLayout !== "quarters") return null
  return {
    error: "zoned_selection_invalid",
    detail: `${itemName ? `"${itemName}"` : "Esta línea"} se armó por zonas y no se puede editar por partes: anúlala y vuelve a agregarla con el armado correcto.`,
    status: 422,
  }
}

/** `code` del 422 con que amend-batch rechaza AGREGAR una pizza por zonas: el POS arma
 *  con él el aviso en su idioma (lib/pos/zonedAmendToast). */
export const ZONED_AMEND_ADD_CODE = "zoned_amend_add" as const

/**
 * Candado de D9 para AGREGAR por enmienda un ítem que se arma por zonas.
 *
 * `rejectZonedAmend` protege lo que ya está vendido; esto, lo que entra. La enmienda de
 * una orden cobrada (amend-batch) no conoce zonas: el POS le manda la línea sin
 * disposición ni zona por ingrediente, y la ruta la asentaría como pizza entera —"sin
 * cebolla" llega como cebolla— cobrada del mapa plano, sin rejilla precio×tamaño, sin
 * fracción de zona y cobrando los incluidos de la receta. Hasta que exista el reemplazo de
 * línea, la pizza se vende en una orden nueva, que sí pasa por `quoteZonedLine`.
 *
 * Quién la llama decide con `items.zoned_build` de la BASE, nunca con el payload: el modal
 * clásico de una tablet con el catálogo viejo manda la pizza sin rastro de zonas.
 */
export function rejectZonedAmendAdd(itemNames: readonly string[]) {
  const label = itemNames.length > 0 ? itemNames.map((n) => `"${n}"`).join(", ") : "Esta pizza"
  return {
    error: "zoned_selection_invalid" as const,
    code: ZONED_AMEND_ADD_CODE,
    detail: `${label} se arma por zonas y no se puede agregar a una orden ya cobrada: cóbrala en una orden nueva.`,
    items: [...itemNames],
    status: 422 as const,
  }
}

function fail(detail: string): ZonedQuoteResult {
  return { ok: false, violation: { error: "zoned_selection_invalid", detail, status: 422 } }
}

export function sizeGridKey(optionId: string, sizeOptionId: string): string {
  return `${optionId}|${sizeOptionId}`
}

/**
 * Precio base CRUDO de una opción en esta línea, antes de `effectiveUnitPrice` y antes
 * de la fracción de zona.
 *
 * D10 — un hueco en la rejilla NO es un 422 en caja: cae a `modifier_options.price_delta`.
 * Con fail-loud puro, agregar un topping nuevo a un ítem zoneado dejaría la rejilla
 * incompleta al instante y produciría 422 masivo en hora pico. La completitud se valida
 * al ACTIVAR el ítem en el panel, que es donde el dueño puede arreglarla.
 *
 * 🔴 LA USA TAMBIÉN EL ARMADOR DEL CLIENTE (components/menu/ZonedBuilder). Es a
 * propósito: mientras la pantalla y la caja llamen a esta misma función, no pueden
 * discrepar. Antes la pantalla resolvía por su cuenta —mostraba el `price_delta`
 * plano sin mirar el tamaño— y en familiar enseñaba $2.99 sobre un pepperoni que se
 * cobraba a $3.99. Por eso `facts` se pide como `Pick<…, "price_delta">` y no
 * entero: para que el cliente pueda llamarla sin fabricar hechos de servidor.
 */
export function resolveBaseCardPrice(
  optionId: string,
  sizeOptionId: string | null,
  facts: Pick<ServerOptionFacts, "price_delta">,
  sizeGrid: Map<string, SizePriceCell>
): number {
  if (sizeOptionId) {
    const cell = sizeGrid.get(sizeGridKey(optionId, sizeOptionId))
    if (cell && typeof cell.price_delta === "number" && Number.isFinite(cell.price_delta)) {
      return cell.price_delta
    }
  }
  return facts.price_delta ?? 0
}

/**
 * El tamaño de la línea: la opción cuyo grupo, EN LA DB, es `items.zone_size_group_id`.
 * Decide qué columna de la rejilla precio×tamaño se cobra en toda la línea.
 *
 * 🔴 NO sale del body. `zone_size_option_id` solo lo manda el armador zoneado: el modal
 * clásico de una tablet que todavía no sabe que el ítem es zoneado, el checkout web y la
 * cuenta abierta del POS mandan el tamaño como un modificador más, y con el campo ausente
 * todo caía al plano de D10 (hallazgo 2026-09-14). Tampoco se le cree cuando viene: una
 * línea editada en el carrito del POS conserva el campo viejo, y un body armado a mano
 * puede declarar mediana para pagar los ingredientes a precio de mediana. El tamaño que se
 * cocina y se cobra como fila es el de los modificadores.
 *
 * - **Ningún tamaño ⇒ `null`**: no hay columna que consultar y cada opción cae a su plano
 *   (D10). No hay 422 propio (el `zoned_size_missing` de §6.4 nunca existió): si el grupo
 *   es obligatorio lo rechaza su mínimo en `validateZonedLoad`, y si el dueño lo dejó
 *   opcional el modal clásico deja pedirla sin tamaño — la caja no puede rechazar lo que
 *   la pantalla permite.
 * - **Dos o más unidades ⇒ violación.** No se elige una: cualquier elección cobraría una
 *   rejilla que nadie pidió. Es el max = 1 por LÍNEA de D5, y corre aunque el ítem no
 *   traiga reglas de grupo.
 *
 * Las remociones no cuentan: quitar no es seleccionar. Una opción que no se resolvió
 * contra la DB tampoco, porque ya no está en `selections`.
 */
function resolveLineSize(
  selections: readonly ZonedSelection[],
  ctx: Pick<ZonedQuoteContext, "sizeGroupId" | "optionFacts">,
  label: string
): { ok: true; sizeOptionId: string | null } | { ok: false; violation: ZonedQuoteViolation } {
  if (!ctx.sizeGroupId) return { ok: true, sizeOptionId: null }
  const sizes = selections.filter(
    (s) => !s.isRemoval && ctx.optionFacts.get(s.optionId)?.group_id === ctx.sizeGroupId
  )
  const units = sizes.reduce((n, s) => n + s.quantity, 0)
  if (units > 1) {
    return {
      ok: false,
      violation: {
        error: "zoned_selection_invalid",
        detail: `${label} solo puede llevar un tamaño (recibidos: ${units}).`,
        status: 422,
      },
    }
  }
  return { ok: true, sizeOptionId: sizes[0]?.optionId ?? null }
}

/**
 * Cotiza una línea zoneada completa. Devuelve las filas listas para insertar, o la
 * PRIMERA violación.
 *
 * ORDEN FIJO — agregar → validar → colapsar (§7). No es intercambiable:
 *  - agregar primero, porque el servidor no puede depender de que el reductor del
 *    cliente ya haya sumado las cantidades por slot; un payload con la misma zona
 *    repetida haría que el colapso vea cobertura total y cobre de menos.
 *  - validar antes de colapsar, porque el colapso BORRA las zonas: un tope de 3 con
 *    `4 left + 4 right` tiene que fallar señalando `left`, no reportar `4 whole`.
 */
export function quoteZonedLine(
  selections: readonly ZonedSelectionInput[],
  ctx: ZonedQuoteContext
): ZonedQuoteResult {
  const label = ctx.itemName ? `"${ctx.itemName}"` : "el artículo"

  // Única regla del modelo que no es garantía de schema (§5): la disposición vive en
  // order_items, y nada en la base impide ponerle 'halves' a un ítem no zoneado.
  if (!ctx.itemZonedBuild && ctx.layout !== "whole") {
    return fail(`${label} no está configurado para armarse por zonas.`)
  }

  const normalized: ZonedSelection[] = []
  for (const s of selections) {
    const facts = ctx.optionFacts.get(s.modifier_option_id)
    // Una opción que no resolvimos contra la DB (cross-tenant, catálogo viejo) NO entra:
    // misma postura server-trusted que checkModifierSelections.
    if (!facts) continue

    const zoneRaw = s.zone ?? "whole"
    if (!VALID_ZONES.includes(zoneRaw)) {
      return fail(`Zona desconocida "${zoneRaw}" en ${label}.`)
    }
    const zone = zoneRaw as Zone

    // Zona ∈ disposición es garantía de Postgres (oim_zone_in_layout), pero rechazarlo
    // acá da un 422 legible en vez de un error de constraint en medio del cobro.
    if (zone !== "whole" && !(ZONES_BY_LAYOUT[ctx.layout] as readonly Zone[]).includes(zone)) {
      return fail(`La zona "${zone}" no existe en una pizza de tipo "${ctx.layout}".`)
    }

    // D8 — el grupo de tamaño y la masa no se zonifican.
    if (zone !== "whole" && facts.zonable !== true) {
      return fail(`"${s.option_name}" no se puede pedir por zona en ${label}.`)
    }

    const qty = Math.max(1, Math.trunc(s.quantity ?? 1))
    normalized.push({
      optionId: s.modifier_option_id,
      zone,
      quantity: qty,
      isRemoval: s.is_removal === true,
      // D6 — un ingrediente no puede estar en la mitad izquierda y en un platito aparte
      // a la vez. Se apaga en servidor en vez de confiar en que la UI lo deshabilitó.
      serveOnSide: zone === "whole" ? s.serve_on_side === true : false,
      // 🔴 D7 — de la DB, NUNCA del payload.
      isIncluded: facts.is_default === true,
    })
  }

  const size = resolveLineSize(normalized, ctx, label)
  if (!size.ok) return { ok: false, violation: size.violation }

  const aggregated = aggregateZonedSelections(normalized)

  const violation = validateZonedLoad(aggregated, ctx, label)
  if (violation) return { ok: false, violation }

  const collapsed = collapseFullCoverage(aggregated, ctx.layout)

  // Metadatos que el colapso no conserva (nombre y grupo son por opción, no por slot).
  const nameByOption = new Map<string, string>()
  const groupByOption = new Map<string, string | null>()
  for (const s of selections) {
    if (!nameByOption.has(s.modifier_option_id)) {
      nameByOption.set(s.modifier_option_id, s.option_name)
      groupByOption.set(s.modifier_option_id, s.modifier_group_id ?? null)
    }
  }

  const rows: ZonedModifierInsert[] = []
  for (const sel of collapsed) {
    const facts = ctx.optionFacts.get(sel.optionId)
    if (!facts) continue

    const baseCardPrice = resolveBaseCardPrice(sel.optionId, size.sizeOptionId, facts, ctx.sizeGrid)

    // §6.5 — el servidor FUERZA 0 en las remociones. Da igual lo que valga la opción:
    // "sin cebolla" sobre una cebolla de $1.50 no puede cobrar $1.50 ni acreditarlo.
    const isRemoval = sel.isRemoval
    const priceDelta = isRemoval
      ? 0
      : resolveZonedUnitPriceCents({
          baseCardPrice,
          zone: sel.zone,
          mode: ctx.mode,
          cfg: ctx.cfg,
          isIncluded: sel.isIncluded,
        }) / 100

    // resolveZonedUnitPriceCents ya tira si baseCardPrice no es finito, pero una
    // remoción se saltea esa llamada — y `numeric` acepta NaN. Sin este guard, un
    // NaN colado por la rama de remoción vuelve NaN todo SUM(price_delta) del turno.
    if (!Number.isFinite(priceDelta)) {
      throw new Error(
        `zonedQuote: price_delta no finito para la opción ${sel.optionId} en zona ${sel.zone}`
      )
    }

    rows.push({
      modifier_option_id: sel.optionId,
      modifier_group_id: groupByOption.get(sel.optionId) ?? facts.group_id ?? null,
      option_name: nameByOption.get(sel.optionId) ?? "",
      quantity: sel.quantity,
      serve_on_side: sel.serveOnSide,
      zone: sel.zone,
      zone_layout: ctx.layout,
      zone_fraction: zoneFraction(sel.zone),
      pricing_mode: ctx.mode,
      is_removal: isRemoval,
      // El crudo también se snapshotea a 0 en una remoción: si guardáramos el precio
      // de la opción, un reporte futuro que multiplique base × cantidad "descubriría"
      // ingresos que nunca existieron.
      base_price_delta: isRemoval ? 0 : effectiveUnitPrice(baseCardPrice, ctx.cfg),
      price_delta: priceDelta,
      size_option_id: size.sizeOptionId,
    })
  }

  return { ok: true, rows }
}

/**
 * §7 — `max_selections` POR ZONA, `min_selections` POR LÍNEA.
 *
 * No son simétricos y tratarlos igual es un bug: "Salsa, min 1" contado por zona
 * exigiría cuatro salsas en `quarters`, en zonas que el cliente ni tocó.
 *
 * 🔴 **La carga efectiva de una zona es `whole` + esa zona.** Un ingrediente en la pizza
 * entera ocupa físicamente la mitad izquierda también. Tratar `whole` como una zona más
 * multiplicaría el tope: con `max = 3` y `quarters` daría `3 whole + 3×4 = 15`
 * ingredientes, 6 de ellos encima de q1. El tope existe por costo y por lo que cabe en
 * una pizza, no por zona lógica.
 */
function validateZonedLoad(
  selections: readonly ZonedSelection[],
  ctx: ZonedQuoteContext,
  label: string
): ZonedQuoteViolation | null {
  const groupRules = ctx.groupRules
  if (!groupRules || groupRules.size === 0) return null

  // Quitar no es seleccionar: las remociones no cuentan para min ni para max.
  const counted = selections.filter((s) => !s.isRemoval)

  const perLine = new Map<string, number>()
  /** clave `groupId|zone`, ya con la carga de `whole` sumada. */
  const perZone = new Map<string, number>()
  const layoutZones = ZONES_BY_LAYOUT[ctx.layout] as readonly Zone[]

  for (const s of counted) {
    const groupId = ctx.optionFacts.get(s.optionId)?.group_id
    if (!groupId) continue
    perLine.set(groupId, (perLine.get(groupId) ?? 0) + s.quantity)

    // `whole` se derrama sobre TODAS las zonas de la disposición.
    const targets: readonly Zone[] = s.zone === "whole" ? layoutZones : [s.zone]
    for (const z of targets) {
      const k = `${groupId}|${z}`
      perZone.set(k, (perZone.get(k) ?? 0) + s.quantity)
    }
  }

  for (const [groupId, rule] of groupRules) {
    // PARIDAD CON EL CLIENTE, igual que checkModifierSelections: is_required=false deja
    // saltarse el grupo, así que el mínimo es 0 aunque min_selections diga otra cosa.
    // Cuando divergieron se cayó Raiz 58 (2026-05-28).
    const min = rule.is_required ? Math.max(1, rule.min_selections ?? 1) : 0
    // max_selections fuera de contrato (0 o negativo) = sin tope, igual que null —
    // mismo criterio que checkModifierSelections (auditoría 2026-07-31). Tomarlo
    // literal volvía 422 permanente cualquier selección del grupo en el flujo zoneado.
    const max = rule.max_selections != null && rule.max_selections > 0 ? rule.max_selections : Infinity

    const got = perLine.get(groupId) ?? 0
    if (got < min) {
      return {
        error: "zoned_selection_invalid",
        detail: `${label} requiere al menos ${min} selecciones en un grupo de modificadores (recibidas: ${got}).`,
        status: 422,
      }
    }

    // El grupo de TAMAÑO lleva max = 1 por LÍNEA, no por zona, o el cliente pide
    // familiar a la izquierda y mediana a la derecha. Ese tope ya lo impuso
    // `resolveLineSize`, con reglas o sin ellas: acá solo se le ahorra el tope por zona.
    if (groupId === ctx.sizeGroupId) continue

    if (!Number.isFinite(max)) continue

    // Zonas a revisar: las de la disposición. En `whole` la única zona ES whole.
    const zonesToCheck: readonly Zone[] = ctx.layout === "whole" ? ["whole"] : layoutZones
    for (const z of zonesToCheck) {
      const load = perZone.get(`${groupId}|${z}`) ?? 0
      if (load > max) {
        return {
          error: "zoned_selection_invalid",
          detail: `${label} admite hasta ${max} en un grupo de modificadores y la zona "${z}" lleva ${load}.`,
          status: 422,
        }
      }
    }
  }

  return null
}
